/**
 * 帮助弹窗展示管理工具（后端驱动）
 *
 * 展示规则完全由后端下发的冷却时间控制：
 *  1. 首次进入页面：后端下发的 windowTime <= 当前时间，自动弹出
 *  2. 点击右上角关闭图标：调用 selectWindow(selectType=1)，进入 7 天冷却期
 *  3. 7 天冷却期结束后再次进入：windowTime <= 当前时间，自动弹出
 *  4. 点击「近期不弹出」：调用 selectWindow(selectType=2)，进入 90 天冷却期
 *  5. 90 天冷却期结束后再次进入：windowTime <= 当前时间，自动弹出
 *  6. Follow / Sending 通过 pageType 区分，冷却状态相互独立
 *
 * 冷却天数（7 天 / 90 天）由后端根据 selectType 计算并写入 windowTime，前端不做本地计算。
 */

import dayjs from 'dayjs'
import { getWindowList, selectWindow } from '@/api/mailWindow'
import { PAGE_KEYS, HELP_DIALOG_ENABLED_CONFIG } from '@/config/help-dialog-config'

// 页面标识 -> 接口 pageType（1-跟进，2-群发）
const PAGE_TYPE_MAP = {
  [PAGE_KEYS.FOLLOW]: 1,
  [PAGE_KEYS.SENDING]: 2
}

// 用户操作类型 -> 接口 selectType
export const SELECT_TYPE = {
  CLOSE_ICON: 1, // 右上角关闭图标：进入 7 天冷却期
  NEVER_AGAIN: 2 // 近期不再弹出按钮：进入 90 天冷却期
}

/**
 * 判断指定页面的帮助模块/入口是否启用（供页面控制问号入口与弹窗的显隐）
 * 未配置的页面默认启用，避免影响后续新增页面
 * @param {string} pageKey - 页面唯一标识
 * @returns {boolean}
 */
export const isHelpDialogEnabled = (pageKey) => HELP_DIALOG_ENABLED_CONFIG[pageKey] !== false

/**
 * 动态设置指定页面的帮助模块开关（用于测试或运行时临时调整；长期开关请改 src/config/help-dialog-config.js）
 * @param {string} pageKey - 页面唯一标识
 * @param {boolean} enabled - 是否启用
 */
export const setHelpDialogEnabled = (pageKey, enabled) => {
  HELP_DIALOG_ENABLED_CONFIG[pageKey] = enabled
}

/**
 * 判断是否应该自动展示帮助弹窗（后端驱动）
 * 规则：取对应 pageType 的记录，当前时间 >= windowTime 时需要弹出
 * @param {string} pageKey - 页面唯一标识（如 'FOLLOW', 'SENDING'）
 * @returns {Promise<boolean>} true 表示应该展示，false 表示不应该展示
 */
export const shouldShowHelpDialog = async (pageKey) => {
  // 模块被配置项隐藏时，不自动弹出
  if (!isHelpDialogEnabled(pageKey)) {
    return false
  }

  const pageType = PAGE_TYPE_MAP[pageKey]
  if (!pageType) {
    return false
  }

  try {
    const res = await getWindowList()
    const list = Array.isArray(res?.result) ? res.result : []
    const target = list.find((item) => Number(item?.pageType) === pageType)

    // 无对应记录（含 result 为空数组）：说明该账号暂未记录弹窗数据，视为首次进入，默认弹出
    if (!target || !target.windowTime) {
      return true
    }

    // 当前时间 >= windowTime 时需要弹出（冷却期已结束）
    return dayjs().valueOf() >= dayjs(target.windowTime).valueOf()
  } catch (e) {
    console.warn('获取帮助弹窗列表失败:', e)
    return false
  }
}

/**
 * 记录用户对帮助弹窗的操作（后端根据 selectType 写入对应冷却时间）
 * @param {string} pageKey - 页面唯一标识（如 'FOLLOW', 'SENDING'）
 * @param {number} selectType - 操作类型，见 SELECT_TYPE
 * @returns {Promise}
 */
export const markHelpDialogSelected = (pageKey, selectType) => {
  const pageType = PAGE_TYPE_MAP[pageKey]
  if (!pageType) {
    return Promise.resolve()
  }

  return selectWindow({ pageType, selectType }).catch((e) => {
    console.warn('更新帮助弹窗状态失败:', e)
  })
}

/**
 * 用户点击右上角关闭图标：进入 7 天冷却期
 * @param {string} pageKey - 页面唯一标识
 * @returns {Promise}
 */
export const markHelpDialogClosedByIcon = (pageKey) =>
  markHelpDialogSelected(pageKey, SELECT_TYPE.CLOSE_ICON)

/**
 * 用户点击「近期不弹出」：进入 90 天冷却期
 * @param {string} pageKey - 页面唯一标识
 * @returns {Promise}
 */
export const markHelpDialogNeverAgain = (pageKey) =>
  markHelpDialogSelected(pageKey, SELECT_TYPE.NEVER_AGAIN)
